/* =========================================================
   Cery Shop - Scanner fotocamera per EAN/SKU nella ricerca.
   ========================================================= */
(() => {
  if (window.__ceryScanner) return;
  window.__ceryScanner = true;

  const ZXING_SRC = 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.1.5/umd/zxing-browser.min.js';
  const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'];

  let modal, video, statusEl;
  let stream = null;
  let detector = null;
  let rafId = null;
  let zxingReader = null;
  let zxingPromise = null;
  let running = false;
  let handledCode = false;

  function getSearchInput() {
    return (
      document.querySelector('#search-modal .search-input') ||
      document.querySelector('.search-input') ||
      document.querySelector('input[name="q"]')
    );
  }

  function buildModal() {
    if (modal) return;
    modal = document.createElement('dialog');
    modal.className = 'cery-scan-dialog';
    modal.innerHTML =
      '<div class="cery-scan__panel">' +
      '<button class="cery-scan__close" data-close aria-label="Chiudi">&times;</button>' +
      '<h3 class="cery-scan__title">Scansiona il codice a barre</h3>' +
      '<p class="cery-scan__hint">Inquadra l\'EAN del prodotto: lo cerco automaticamente nel negozio.</p>' +
      '<div class="cery-scan__viewport"><video data-video playsinline muted></video><div class="cery-scan__reticle"></div></div>' +
      '<p class="cery-scan__status" data-status></p>' +
      '</div>';
    document.body.appendChild(modal);
    video = modal.querySelector('[data-video]');
    statusEl = modal.querySelector('[data-status]');
    modal.querySelector('[data-close]').addEventListener('click', closeScanner);
    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeScanner();
    });
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeScanner();
    });
  }

  function setStatus(message, type) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.dataset.type = type || '';
  }

  function openScanner() {
    buildModal();
    if (!modal.open) modal.showModal();
    setStatus('');
    startCamera();
  }

  function closeScanner() {
    stopCamera();
    if (modal && modal.open) modal.close();
  }

  function startCamera() {
    running = true;
    handledCode = false;

    if ('BarcodeDetector' in window && navigator.mediaDevices?.getUserMedia) {
      startNativeDetector();
      return;
    }

    startZxing();
  }

  function stopStream() {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
  }

  function stopNativeDetector() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    detector = null;
    stopStream();
    if (video) video.srcObject = null;
  }

  function stopCamera() {
    running = false;
    stopNativeDetector();

    if (zxingReader) {
      try {
        zxingReader.reset();
      } catch (error) {}
      zxingReader = null;
    }
  }

  async function startNativeDetector() {
    setStatus('Avvio fotocamera...');

    try {
      const supportedFormats =
        typeof window.BarcodeDetector.getSupportedFormats === 'function'
          ? await window.BarcodeDetector.getSupportedFormats()
          : BARCODE_FORMATS;
      const formats = BARCODE_FORMATS.filter((format) => supportedFormats.includes(format));

      if (!formats.length) throw new Error('No supported barcode formats');

      detector = new window.BarcodeDetector({ formats });
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (!running) {
        stopNativeDetector();
        return;
      }

      video.srcObject = stream;
      await video.play();
      setStatus('Inquadra il codice...');
      scanNativeFrame();
    } catch (error) {
      stopNativeDetector();
      if (running) startZxing();
    }
  }

  async function scanNativeFrame() {
    if (!running || !detector || !video) return;

    try {
      if (video.readyState >= 2) {
        const barcodes = await detector.detect(video);
        const match = barcodes.find((barcode) => barcode && barcode.rawValue);

        if (match) {
          handleCode(match.rawValue);
          return;
        }
      }

      rafId = requestAnimationFrame(scanNativeFrame);
    } catch (error) {
      stopNativeDetector();
      if (running) startZxing();
    }
  }

  function loadZxing() {
    if (window.ZXingBrowser) return Promise.resolve();
    if (zxingPromise) return zxingPromise;

    zxingPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-cery-zxing]');
      if (existing) {
        if (window.ZXingBrowser) {
          resolve();
          return;
        }

        if (existing.dataset.loaded === 'true') {
          existing.remove();
        } else {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
      }

      const script = document.createElement('script');
      script.src = ZXING_SRC;
      script.async = true;
      script.defer = true;
      script.dataset.ceryZxing = 'true';
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = (event) => {
        script.remove();
        zxingPromise = null;
        reject(event);
      };
      document.head.appendChild(script);
    });

    return zxingPromise;
  }

  function startZxing() {
    setStatus(window.ZXingBrowser ? 'Avvio fotocamera...' : 'Preparo scanner...');

    loadZxing()
      .then(() => {
        if (!running) return;

        try {
          zxingReader = new window.ZXingBrowser.BrowserMultiFormatReader(undefined, {
            delayBetweenScanAttempts: 80,
            delayBetweenScanSuccess: 250,
          });
          const decoding = zxingReader.decodeFromConstraints(
            {
              audio: false,
              video: {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
            },
            video,
            (result) => {
              if (result && running) handleCode(result.getText());
            }
          );

          if (decoding && typeof decoding.catch === 'function') {
            decoding.catch(() => {
              if (running) setStatus('Scansione non supportata: digita il codice nella barra di ricerca.', 'warn');
            });
          }

          setStatus('Inquadra il codice...');
        } catch (error) {
          setStatus('Scansione non supportata: digita il codice nella barra di ricerca.', 'warn');
        }
      })
      .catch(() => {
        setStatus('Scansione non supportata: digita il codice nella barra di ricerca.', 'warn');
      });
  }

  function dispatchSearchInput(input, code) {
    input.focus({ preventScroll: true });
    input.value = code;

    let inputEvent;
    try {
      inputEvent = new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertReplacementText',
        data: code,
      });
    } catch (error) {
      inputEvent = new Event('input', { bubbles: true, cancelable: true });
    }

    input.dispatchEvent(inputEvent);
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const predictiveSearch = input.closest('predictive-search-component');
    if (predictiveSearch && typeof predictiveSearch.search === 'function') {
      predictiveSearch.search(inputEvent);
    }
  }

  let lastCode = '';
  let lastTime = 0;
  function handleCode(code) {
    code = ('' + code).trim();
    if (!code || handledCode) return;

    const now = Date.now();
    if (code === lastCode && now - lastTime < 2000) return;

    lastCode = code;
    lastTime = now;
    handledCode = true;

    setStatus('Trovato: ' + code, 'ok');
    if (navigator.vibrate) navigator.vibrate(40);
    closeScanner();

    const input = getSearchInput();
    if (input) {
      dispatchSearchInput(input, code);
    } else {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root.replace(/\/$/, '') : '';
      window.location.href = root + '/search?q=' + encodeURIComponent(code);
    }
  }

  function warmScannerLibrary() {
    if (!('BarcodeDetector' in window)) {
      loadZxing().catch(() => {});
    }
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(warmScannerLibrary, { timeout: 2500 });
  } else {
    window.addEventListener('load', () => setTimeout(warmScannerLibrary, 1200), { once: true });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-cery-scan]') : null;
    if (!target) return;

    event.preventDefault();
    openScanner();
  });
})();
